import { PortableText, type PortableTextBlock } from '@portabletext/react'
import { formatInTimeZone } from 'date-fns-tz'
import { data, Link } from 'react-router'
import { SanityImage } from '#app/components/ui/SanityImage.tsx'
import {
	type Event,
	type SanityImageCrop,
	type SanityImageHotspot,
	type Venue,
} from '#app/types/sanity.types.ts'
import { safeFetch } from '#app/utils/sanity.server.ts'
import { type Route } from './+types/$eventId.ts'

type ResolvedImage = {
	asset: {
		_id: string
		metadata?: {
			dimensions: {
				width: number
				height: number
				aspectRatio: number
			}
		}
	}
	hotspot?: SanityImageHotspot
	crop?: SanityImageCrop
	alt?: string
	caption?: string
	_type: 'image'
	_key: string
}

type ResolvedVenue = Omit<Venue, 'thumbnail'> & {
	thumbnail?: ResolvedImage
}

type EventDetail = Pick<
	Event,
	| '_id'
	| 'name'
	| 'displayName'
	| 'eventType'
	| 'useDate'
	| 'date'
	| 'dateRange'
	| 'description'
> & {
	extendedDescription?: PortableTextBlock[]
	eventPhotos?: PortableTextBlock[]
	venue: ResolvedVenue | null
}

export async function loader({ params }: Route.LoaderArgs) {
	const query = `*[_type == "event" && _id == $id][0] {
		_id,
		name,
		displayName,
		useDate,
		date,
		dateRange,
		eventType,
		description,
		extendedDescription[]{
			...,
			_type == "image" => {
				...,
				asset-> {
					_id,
					metadata {
						dimensions {
							width,
							height,
							aspectRatio,
						}
					}
				}
			}
		},
		eventPhotos[]{
			...,
			_type == "image" => {
				...,
				asset-> {
					_id,
					metadata {
						dimensions {
							width,
							height,
							aspectRatio,
						}
					}
				}
			}
		},
		"venue": location-> {
			_id,
			displayName,
			address,
			city,
			state,
			zip,
			map,
			website,
			thumbnail {
				...,
				asset-> {
					_id,
					metadata {
						dimensions {
							width,
							height,
							aspectRatio,
						}
					}
				}
			}
		},
	}`

	const event = await safeFetch<EventDetail | null>(query, {
		id: params.eventId,
	})

	if (!event) {
		throw data('Event not found', { status: 404 })
	}

	return { event }
}

export default function EventDetailPage({ loaderData }: Route.ComponentProps) {
	const { event } = loaderData
	const title = event.displayName ?? event.name

	const portableTextComponents = {
		types: {
			image: ({ value }: { value: ResolvedImage }) => {
				if (!value.asset) return null
				return (
					<figure className="my-4">
						<SanityImage
							source={value}
							alt={value.alt ?? ''}
							className="rounded-lg"
						/>
						{value.caption ? (
							<figcaption className="mt-1 text-center text-sm text-slate-500 dark:text-slate-400">
								{value.caption}
							</figcaption>
						) : null}
					</figure>
				)
			},
		},
	}

	return (
		<main className="font-poppins mx-auto w-full max-w-3xl px-4 py-8">
			<Link
				to="/events"
				className="mb-8 inline-block text-slate-600 underline dark:text-slate-400"
			>
				← Back to event list
			</Link>

			<h1 className="text-scbg-gold mt-4 text-4xl font-bold">{title}</h1>

			{event.eventType ? (
				<div className="mt-1 text-slate-600 dark:text-slate-400">
					{event.eventType.charAt(0).toUpperCase() + event.eventType.slice(1)}{' '}
					Backgammon
				</div>
			) : null}

			<div className="mt-6 space-y-2 text-slate-800 dark:text-slate-200">
				{event.useDate === 'date' ? (
					event.date ? (
						<div>
							{formatInTimeZone(
								new Date(event.date),
								'America/New_York',
								'EEEE, MMMM d, yyyy · h:mm aaa zzz',
							)}
						</div>
					) : null
				) : event.dateRange ? (
					<div>{event.dateRange}</div>
				) : null}

				{event.venue ? (
					<div className="flex gap-4">
						<div className="space-y-1">
							{event.venue.displayName ? (
								<div>{event.venue.displayName}</div>
							) : null}
							{event.venue.address ? <div>{event.venue.address}</div> : null}
							{event.venue.city ? (
								<div>
									{event.venue.city}
									{event.venue.state ? `, ${event.venue.state}` : ''}
									{event.venue.zip ? ` ${event.venue.zip}` : ''}
								</div>
							) : null}
							{event.venue.map || event.venue.website ? (
								<div className="flex gap-2 pt-1">
									{event.venue.map ? (
										<Link
											to={event.venue.map}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center gap-2 rounded-md bg-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 no-underline transition-colors hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-200"
										>
											Directions
										</Link>
									) : null}
									{event.venue.website ? (
										<Link
											to={event.venue.website}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center gap-2 rounded-md bg-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 no-underline transition-colors hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-200"
										>
											Website
										</Link>
									) : null}
								</div>
							) : null}
						</div>
						{event.venue.thumbnail ? (
							<div className="ml-auto shrink-0 basis-1/4">
								<SanityImage
									source={event.venue.thumbnail}
									alt={event.venue.displayName ?? ''}
									className="rounded-xl shadow-lg"
								/>
							</div>
						) : null}
					</div>
				) : null}

				{event.description ? <div>{event.description}</div> : null}
			</div>

			{event.extendedDescription && event.extendedDescription.length > 0 ? (
				<div
					id="event-description"
					className="prose prose-slate dark:prose-invert mt-8 max-w-none"
				>
					<PortableText
						value={event.extendedDescription}
						components={portableTextComponents}
					/>
				</div>
			) : null}

			{event.eventPhotos && event.eventPhotos.length > 0 ? (
				<div
					id="event-photos"
					className="prose prose-slate dark:prose-invert mt-8 max-w-none"
				>
					<PortableText
						value={event.eventPhotos}
						components={portableTextComponents}
					/>
				</div>
			) : null}
		</main>
	)
}
